import { Link } from "react-router-dom";
import { Minus, Plus, X, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { Reveal } from "@/components/Reveal";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const shipping = subtotal >= 75 || subtotal === 0 ? 0 : 6;

  return (
    <div className="min-h-screen bg-ivory px-5 py-16 md:px-10 md:py-24" data-testid="cart-page">
      <div className="mx-auto max-w-[1200px]">
        <h1 className="font-serif text-5xl font-light tracking-tight text-ink md:text-7xl">Your Bag</h1>

        {items.length === 0 ? (
          <Reveal className="mt-16 flex flex-col items-center gap-6 text-center">
            <ShoppingBag size={44} strokeWidth={1} className="text-sand" />
            <p className="text-ink/60">Your bag is currently empty.</p>
            <Link
              to="/shop"
              className="rounded-full bg-ink px-9 py-4 text-[0.72rem] uppercase tracking-[0.24em] text-ivory transition-colors hover:bg-gold"
            >
              Discover Jewelry
            </Link>
          </Reveal>
        ) : (
          <div className="mt-12 grid gap-12 lg:grid-cols-[1.6fr_1fr]">
            <div className="divide-y divide-black/5 border-y border-black/5">
              {items.map((i) => (
                <div key={i.slug} className="flex gap-5 py-6" data-testid={`cart-row-${i.slug}`}>
                  <Link to={`/product/${i.slug}`}>
                    <img src={i.image} alt={i.name} className="h-32 w-28 rounded-xl object-cover" />
                  </Link>
                  <div className="flex flex-1 flex-col">
                    <div className="flex justify-between">
                      <Link to={`/product/${i.slug}`} className="font-serif text-2xl text-ink">
                        {i.name}
                      </Link>
                      <button onClick={() => removeItem(i.slug)} aria-label="Remove" data-testid={`remove-${i.slug}`}>
                        <X size={18} strokeWidth={1.5} className="text-ink/40 hover:text-ink" />
                      </button>
                    </div>
                    <p className="mt-1 text-sm text-ink/60">${i.price.toFixed(0)}</p>
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center gap-4 rounded-full border border-sand px-4 py-2">
                        <button onClick={() => updateQty(i.slug, i.quantity - 1)} data-testid={`dec-${i.slug}`}>
                          <Minus size={13} />
                        </button>
                        <span className="w-5 text-center text-sm">{i.quantity}</span>
                        <button onClick={() => updateQty(i.slug, i.quantity + 1)} data-testid={`inc-${i.slug}`}>
                          <Plus size={13} />
                        </button>
                      </div>
                      <span className="font-serif text-xl text-ink">
                        ${(i.price * i.quantity).toFixed(0)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="h-fit rounded-2xl bg-cream p-8 lg:sticky lg:top-32">
              <h2 className="font-serif text-3xl text-ink">Summary</h2>
              <div className="mt-6 space-y-3 text-sm">
                <div className="flex justify-between text-ink/70">
                  <span>Subtotal</span>
                  <span data-testid="summary-subtotal">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-ink/70">
                  <span>Shipping</span>
                  <span>{shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}</span>
                </div>
                <div className="mt-4 flex justify-between border-t border-black/10 pt-4 font-serif text-2xl text-ink">
                  <span>Total</span>
                  <span data-testid="summary-total">${(subtotal + shipping).toFixed(2)}</span>
                </div>
              </div>
              <Link
                to="/checkout"
                className="mt-8 block rounded-full bg-ink py-4 text-center text-[0.72rem] uppercase tracking-[0.24em] text-ivory transition-colors hover:bg-gold"
                data-testid="cart-page-checkout"
              >
                Proceed to Checkout
              </Link>
              <Link to="/shop" className="mt-4 block text-center eyebrow text-ink/60 link-underline">
                Continue Shopping
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
